/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  CheckoutError,
  CheckoutPending,
  CheckoutSuccess,
} from '@ahamove/icons/checkout';
import { color } from '@ahamove/tokens';
import type {
  DefaultPaymentResponse,
  PaymentMethod,
} from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import {
  Box,
  Button,
  CloseButton,
  Group,
  LoadingOverlay,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { closeAllModals, type ContextModalProps } from '@mantine/modals';
import { unix } from 'dayjs';
import { useRouter } from 'next/navigation';
import { cloneElement, useCallback, useEffect, useState } from 'react';
import { PayStatus } from './modal-checkout';
import { ROUTES } from '@/config/router';
import {
  SubscriptionTab,
  useSubscriptionsStore,
} from '@/features/booking/store';
import { useCheckoutStore } from '@/features/checkout/store';
import { useGetProgressesSubscriptionList } from '@/features/subscription/api';
import { CheckoutType } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useDetectModalStore } from '@/stores';

export type ModalCheckoutResultProps = ContextModalProps<{
  transaction: DefaultPaymentResponse;
  paymentMethod: PaymentMethod | null;
  requestBody: any;
  isAutoClose?: boolean;
}>;

const ModalCheckoutResult = ({
  id: modalId,
  innerProps,
}: ModalCheckoutResultProps) => {
  const router = useRouter();
  const { transaction, requestBody, isAutoClose = true } = innerProps;
  const isMobile = useIsMobile();
  const method =
    innerProps?.paymentMethod ?? useCheckoutStore.use.checkoutPaymentMethod();
  const localeSuffix = useLocaleSuffix();
  const setDetectModalId = useDetectModalStore.use.setDetectModalId();
  const clearDetectModalId = useDetectModalStore.use.clearDetectModalId();
  const setActiveTab = useSubscriptionsStore.use.setActiveTab();
  const [isNavigating, setIsNavigating] = useState<boolean>(false);

  // Auto close states
  const [countdown, setCountdown] = useState(30);
  const [isAutoClosing, setIsAutoClosing] = useState(false);

  const { data: progressesSubscriptionList, isLoading } =
    useGetProgressesSubscriptionList({
      enabled:
        transaction.status === PayStatus.success &&
        requestBody?.payment_type === CheckoutType.Subscription,
      staleTime: 3000,
    });

  const handleRedirectSubscription = useCallback(() => {
    if (
      requestBody?.payment_type === CheckoutType.Subscription &&
      requestBody?.plan_id &&
      requestBody?.subscription_id
    ) {
      if (
        progressesSubscriptionList?.data &&
        progressesSubscriptionList?.data?.length > 0
      ) {
        const findProgressesSubscription =
          progressesSubscriptionList?.data?.find(
            (item) =>
              item.subscription_id === requestBody.subscription_id &&
              item._id.includes(requestBody.plan_id)
          );
        if (!findProgressesSubscription) {
          setActiveTab(SubscriptionTab.my_subscriptions);
          router.push(ROUTES.subscriptions);
        } else {
          router.push(
            ROUTES.mySubscriptionDetail(
              findProgressesSubscription._id as string
            )
          );
        }
      } else {
        router.push(ROUTES.subscriptionDetail(requestBody.subscription_id));
      }
    } else {
      return router.push(ROUTES.subscriptions);
    }
    closeAllModals();
  }, [
    requestBody.subscription_id,
    requestBody.plan_id,
    requestBody.payment_type,
    progressesSubscriptionList?.data,
    router,
  ]);

  const handleCloseModal = useCallback(() => {
    // If isAutoClose is false, just close the modal without any redirect
    if (isAutoClose === false) {
      closeAllModals();
      return;
    }

    const isSuccess = transaction.status === PayStatus.success;

    switch (requestBody.payment_type) {
      case CheckoutType.Topup:
        closeAllModals();
        return;

      case CheckoutType.Subscription:
        if (isSuccess) {
          setActiveTab(SubscriptionTab.subscription);
          router.push(ROUTES.subscriptions);
        } else {
          router.push(ROUTES.subscriptionDetail(requestBody.subscription_id));
        }
        closeAllModals();
        return;

      default:
        if (isSuccess && requestBody.order_id) {
          if (requestBody.callback) {
            requestBody.callback();
          }
          setIsNavigating(true);
          closeAllModals();
          setTimeout(() => {
            router.push(ROUTES.recordDetail(requestBody.order_id));
          }, 100);
        } else {
          closeAllModals();
        }
    }
  }, [
    transaction.status,
    requestBody.payment_type,
    requestBody.subscription_id,
    requestBody.order_id,
    setActiveTab,
    router,
    isAutoClose,
  ]);

  useEffect(() => {
    setDetectModalId(modalId);
    return () => {
      clearDetectModalId();
    };
  }, [modalId, setDetectModalId, clearDetectModalId]);

  // Auto close countdown effect
  useEffect(() => {
    if (transaction.status === PayStatus.success && isAutoClose) {
      setIsAutoClosing(true);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleCloseModal();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [transaction.status, handleCloseModal, isAutoClose]);

  const statusLabelArr = [
    {
      status: PayStatus.success,
      icon: <CheckoutSuccess />,
      bgColor: color.green[20],
      title: 'Thanh toán thành công',
    },
    {
      status: PayStatus.failed,
      icon: <CheckoutError />,
      bgColor: color.red[30],
      title: 'Thanh toán thất bại',
    },
    {
      status: PayStatus.confirming,
      icon: <CheckoutPending />,
      bgColor: color.yellow[30],
      title: 'Đang xử lý',
    },
  ];

  const matchedStatus = statusLabelArr.find(
    (item) => item.status === transaction.status
  );

  return (
    <>
      <LoadingOverlay
        visible={isNavigating}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ color: 'primary', type: 'bars' }}
      />
      <Group
        pos={isMobile ? 'fixed' : 'sticky'}
        h={isMobile ? rem(48) : rem(64)}
        top={0}
        left={0}
        justify="center"
        w="100%"
        px={isMobile ? rem(8) : rem(16)}
        py={isMobile ? rem(4) : 0}
        bg="white"
        styles={
          isMobile
            ? {
                root: {
                  zIndex: 200,
                  borderBottom: '1px solid #DDE1E6',
                },
              }
            : {
                root: {
                  zIndex: 200,
                  boxShadow: 'none',
                },
              }
        }
      >
        <Title order={4} c="neutral.7">
          Chi tiết thanh toán
        </Title>

        <CloseButton
          pos="absolute"
          size={rem(40)}
          variant="transparent"
          onClick={handleCloseModal}
          c="neutral.6"
          disabled={isNavigating}
          style={
            isMobile
              ? {
                  left: rem(6),
                  outline: 'none',
                }
              : {
                  right: rem(6),
                  outline: 'none',
                }
          }
        />
      </Group>

      <Stack gap={rem(24)} p="md" mt={isMobile ? rem(48) : 'none'}>
        <Stack
          align="center"
          bg={`linear-gradient(359deg, #FFF 53.15%, ${matchedStatus?.bgColor} 160.06%)`}
          py={rem(24)}
          px={rem(12)}
          style={{ borderRadius: rem(8) }}
        >
          {matchedStatus?.icon ? (
            <Box w={rem(100)} h={rem(100)}>
              {cloneElement(matchedStatus?.icon, {
                style: { width: '100%', height: '100%' },
              })}
            </Box>
          ) : null}

          <Text size={rem(20)} lh={rem(28)} fw={700} c="#012749">
            {matchedStatus?.title}
          </Text>
          <Text size={rem(32)} lh={rem(40)} fw={700} c="#0E4174">
            {formatCurrency(requestBody?.amount ?? 0)}
          </Text>
        </Stack>

        <Stack gap={rem(8)}>
          <Group justify="space-between" align="flex-start">
            <Text size={isMobile ? 'sm' : 'md'} fw={500} c="'neutral.6">
              {requestBody.payment_type === CheckoutType.Subscription
                ? 'Tên gói'
                : 'Loại giao dịch'}
            </Text>
            <Text
              size={isMobile ? 'sm' : 'md'}
              fw={600}
              c="neutral.7"
              maw="60%"
              ta="right"
            >
              {requestBody?.title}
            </Text>
          </Group>
          <Group justify="space-between" align="flex-start">
            <Text size={isMobile ? 'sm' : 'md'} fw={500} c="'neutral.6">
              Mã giao dịch
            </Text>
            <Text
              size={isMobile ? 'sm' : 'md'}
              fw={600}
              c="neutral.7"
              maw="60%"
              lineClamp={1}
              ta="right"
            >
              {transaction?.data?.payment_provider_log_id || 'N/A'}
            </Text>
          </Group>
          <Group justify="space-between" align="flex-start">
            <Text size={isMobile ? 'sm' : 'md'} fw={500} c="'neutral.6">
              Thời gian
            </Text>
            <Text
              size={isMobile ? 'sm' : 'md'}
              fw={600}
              c="neutral.7"
              maw="60%"
              lineClamp={1}
              ta="right"
            >
              {unix(Number(transaction.transaction_time)).format(
                'DD/MM/YYYY, HH:mm'
              )}
            </Text>
          </Group>
          <Group justify="space-between" align="flex-start">
            <Text size={isMobile ? 'sm' : 'md'} fw={500} c="'neutral.6">
              Phương thức thanh toán
            </Text>
            <Group gap={rem(8)} maw="50%">
              {method?.icon_url && (
                <img
                  src={method.icon_url}
                  alt={method?.[`name_${localeSuffix}`] || 'Payment method'}
                  style={{
                    width: rem(20),
                    height: rem(20),
                    objectFit: 'contain',
                  }}
                />
              )}
              <Text
                size={isMobile ? 'sm' : 'md'}
                fw={600}
                c="neutral.7"
                lineClamp={1}
                ta="right"
              >
                {method?.[`name_${localeSuffix}`] || 'N/A'}
              </Text>
            </Group>
          </Group>
        </Stack>

        {/* Action Button */}
        <Box
          px={isMobile ? rem(16) : 0}
          py={isMobile ? rem(24) : 0}
          style={{
            position: isMobile ? 'fixed' : 'inherit',
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <Button
            loading={isLoading}
            onClick={
              requestBody.payment_type === CheckoutType.Subscription &&
              transaction.status === PayStatus.success
                ? handleRedirectSubscription
                : handleCloseModal
            }
            fullWidth
            color={
              transaction.status === PayStatus.success ? 'primary' : 'default'
            }
            variant={
              transaction.status === PayStatus.success ? 'filled' : 'light'
            }
            size="md"
            mih={rem(48)}
          >
            {requestBody.payment_type === CheckoutType.Subscription
              ? 'Xem quyền lợi gói'
              : 'Đóng'}
          </Button>

          {/* Countdown text below button for successful payments */}
          {transaction.status === PayStatus.success && isAutoClosing && (
            <Text size="xs" ta="center" c="neutral.6" mt={rem(8)}>
              ({countdown}s)
            </Text>
          )}
        </Box>
      </Stack>
    </>
  );
};
export default ModalCheckoutResult;
